import React from 'react';
import {
  LipidParticleMap,
  ldlPBandIndex,
  smallLdlPBandIndex,
  smallLdlPBandLabel,
} from '../../models/LipidParticleMap';
import { MARKER_BAR_PALETTE } from '../charts/chartPalette';

/**
 * The Particle Map: where a client falls on Labcorp's two printed count axes.
 *
 * Inline SVG rather than a chart library, for the reason the Insights diagrams
 * generally are: this is a grid of fixed cells, not a plot of data, and
 * Highcharts would cost a second renderer in the PDF path for twenty
 * rectangles. The Insights column is a fixed 300px (`.evaluationPanel`) with
 * card padding either side, so everything here is drawn to a 262px box.
 *
 * ── Five columns and four rows, not 2x2 ────────────────────────────────────
 * Maryann, 2026-10-26: "we're just doing two columns ... but they have various
 * levels, so maybe we should have multiple columns based on their different
 * levels." Both axes turned out to have printed levels, which is what made it
 * possible:
 *
 *   LDL-P        Low <1000 | Moderate 1000-1299 | Borderline-High 1300-1599 |
 *                High 1600-2000 | Very High >2000
 *   Small LDL-P  Low <117 | 117 | 527 | 839 | High >839
 *
 * The cells carry no text. Twenty 43x22 cells cannot hold a phrase, and the
 * cell is NAMED in the headline above the grid instead, from the lab's own two
 * band words (`particleCellName`). The client's position is the blood drop,
 * which is the marker every reference-range bar in the app already uses.
 *
 * ── The colors are DISPLAY ONLY, and they read in TWO directions ──────
 * Severity rises across the grid with the LDL-P band and again down it with
 * the small-particle count, so the best cell is top-left and the worst is
 * bottom-right.
 *
 * ⚠ THIS REPLACED A COLUMN-ONLY RULE ON 2026-10-26, and the rule it replaced
 * was wrong on its own citation. The old comment here read "Mora 2007: size
 * loses significance once both LDL subclasses are in the model" and then
 * flattened the ROW on the strength of it. What Mora 2007 found lose
 * significance was LDL SIZE, the mean diameter (p=0.10). With both subclasses
 * in the model the small COUNT was significant and carried the LARGER
 * coefficient of the two: 52.2 um of carotid IMT per 1 SD against large
 * LDL-P's 36.6, both p<0.001 (PMID: 16765964). Diameter and small count are
 * two different markers and the old comment ran them together.
 *
 * Maryann, 2026-10-26: "they should be shaded if it's a ... borderline high
 * LDL particles but small size, like low. That's better than a high number of
 * smalls." That is what the outcome data says too:
 *
 *   - Hoogeveen 2014, ARIC, n=11,419: small dense LDL-C predicted incident CHD
 *     even among people who looked LOW risk on LDL-C (HR 1.61) — PMID: 24558110.
 *   - Ikezaki 2021, Framingham Offspring: once small dense LDL-C was in the
 *     model no other lipoprotein parameter added information — PMID: 33586462.
 *   - Zubirán 2025, UK Biobank, n=271,760: adjusted for apoB, LDL-C's own
 *     association REVERSED while small dense LDL-C stayed positive.
 *
 * The row shifts by ONE step rather than taking a max, because the column is
 * still the dominant axis and the honest reading is "the row carries less,"
 * not "the row carries nothing":
 *
 *   - Labcorp's own footnote says "Small LDL-P and LDL Size are associated
 *     with CVD risk, but not after LDL-P is taken into account."
 *   - Morze 2025, UK Biobank, n=207,368: size subclasses were not associated
 *     with CAD after apoB particle adjustment — PMID: 40289348.
 *   - Tsai 2013, MESA: the chemical small dense LDL-C assay predicted CHD but
 *     the NMR small-LDL-P count, which is the number THIS row plots, did not.
 *
 * A client with LDL-P in range and a raised small count lands on a YELLOW cell,
 * and the box below AGREES: that combination draws pattern 2.1 or 2.2, both of
 * which are `flag`. Hoogeveen is exactly that client. Pure green overstated the
 * reassurance.
 *
 * ⚠ THE COLUMN-ONLY RULE ALSO CLAIMED THIS CELL WOULD CONTRADICT PATTERN 2.3.
 * It would not. 2.3 requires `c.eq('smallLdlParticleNumber')`, meaning the small
 * count is tested and INSIDE its range; it is about a small mean DIAMETER
 * carried on two counts that are both fine. It never fires for a raised small
 * count, so it was never in tension with shading this row. Check the `when`
 * clause before citing a pattern's effect here.
 *
 * Nothing here grades a marker or feeds a root cause; `lipidParticleMap()`
 * returns the position and this only paints it.
 *
 * ── Why the LP-IR strip left this file ─────────────────────────────────────
 * It is a gauge now, drawn by the card above this grid (see
 * lipid-particle-insights.tsx). The earlier objection here was to
 * LabsmartsGuage's DEFAULTS, its hard-coded 0/50/70/90 bands and its "(n%)"
 * likelihood readout, not to the dial; both are overridable props as of
 * 2026-10-26, so the house gauge draws Labcorp's own 27/45/63 axis and reads
 * the score rather than a percentage.
 */

const W = 262;

/**
 * Row labels, top to bottom: the small count increases DOWNWARD.
 *
 * ⚠ THESE WERE PERCENTILES ("25–50th", "50–75th") FOR PART OF 2026-10-26.
 * Maryann: "if we're going to have them, why do we even have the percentiles
 * then? ... what if we had low, below average, above average, and high instead
 * of the percentiles?" One vocabulary now: the grid, the headline and the
 * bullets all say the same four words, and the percentile each one stands for
 * lives in the header tooltip.
 *
 * Abbreviated to "Avg" because the full words do not fit. Measured: "Below
 * Average" at 9px bold is 64.9px, which forces a 99px gutter and squeezes the
 * cells to 30.2px, too narrow for the 29.5px "B-High" column label. "Below
 * Avg" is 46.4px and leaves the cells at 35.6px.
 */
const ROW_LABELS = ['Low', 'Below Avg', 'Above Avg', 'High'];

/**
 * Column labels, abbreviated to fit a 35px cell at 9px.
 *
 * "B-High" and "V.High" rather than the full band names, which is the same
 * compromise the marker row already makes (TG/HDL, HbA1c). The unabbreviated
 * name is in the headline and in the header tooltip, both of which have room.
 */
const COL_LABELS = ['Low', 'Mod', 'B-High', 'High', 'V.High'];

/*
 * \u26a0 ONE LABEL PER PRINTED BAND, on both axes. The labels are abbreviated so
 * they cannot be derived from LDL_P_BANDS / SMALL_LDL_P_BANDS, so the counts
 * are asserted in lipid-particle-hero.render.test.tsx instead: a sixth LDL-P
 * band added to the model would otherwise draw five columns and silently
 * swallow it.
 */

/**
 * The severity ramp the cells are painted from, best to worst.
 *
 * Five steps for five LDL-P bands, four colors: Labcorp's top two bands are
 * both the High family, so they share red and are told apart by position. The
 * row shift can push a Very-High column past the end of the ramp, which is
 * what the clamp in `cellSeverity` is for.
 *
 * MARKER_BAR_PALETTE rather than MUTED_PALETTE, for the reason that palette
 * documents: these cells are 22px tall inside a busy card, and at the muted
 * alpha the green and yellow washed out.
 */
const SEVERITY_FILL = [
  MARKER_BAR_PALETTE.green,
  MARKER_BAR_PALETTE.yellow,
  MARKER_BAR_PALETTE.orange,
  MARKER_BAR_PALETTE.red,
  MARKER_BAR_PALETTE.red,
];

/**
 * What each small-particle row does to the column's own level.
 *
 * Below the 25th percentile comes back a step, above the 75th goes forward a
 * step, and the two middle rows sit where the column puts them. One step, not
 * a max of the two axes: see the header.
 */
const ROW_SHIFT = [-1, 0, 0, 1];

/** Where a cell sits on SEVERITY_FILL, from its column and its row. */
export function cellSeverity(col: number, row: number): number {
  return Math.max(
    0,
    Math.min(SEVERITY_FILL.length - 1, col + ROW_SHIFT[row]),
  );
}

const COL_AXIS_TITLE = 'LDL-P';
/**
 * ⚠ ONE LINE, AND NO LONGER THE WORD "PERCENTILE" (Maryann, 2026-10-26: "we
 * can remove the word 'percentiles' in the vertical axis label"). It read
 * "Small LDL-P" / "percentile" for part of that day, when the rows below it
 * were percentiles and needed saying so. The rows are words now, so the title
 * is just the marker name, which also makes it the mirror of "LDL-P" on top.
 */
const ROW_AXIS_TITLE = ['Small LDL-P'];

/*
 * The left gutter holds the rotated title, then the row labels, right-aligned
 * to GUTTER-5.
 *
 * MEASURED IN HEADLESS CHROME, not estimated: a rotated 11px title line renders
 * 13px wide and "Below Avg" at 9px bold is 46.4px, so 13 + 4 + 46.4 + 5 rounds
 * to 72. That leaves the cells at 35.6px, which still holds the widest column
 * label ("B-High", 29.5px).
 */
const GUTTER = 72;
const GAP = 3;
const CELL_H = 22;
/** Room above the grid for the column title (y=11) and the column labels (y=25). */
const GRID_TOP = 32;
/*
 * \u26a0 THESE ARE NOT THE VISUAL CENTERS of the two rotated lines.
 *
 * `rotate(-90)` maps a point (dx, dy) to (dy, -dx), so the text's VERTICAL
 * extent about its baseline becomes its horizontal extent: a line ends up
 * reaching about one ascender (9-10px at 10px type) to the LEFT of the x given
 * here and only its descender (about 2px) to the right. Measured: x=7 renders
 * from -3.0 to 9.0, which ran off the left edge of the SVG.
 *
 * So each value is roughly the line's RIGHT edge, not its middle. 12 and 27
 * render at 2-14 and 17-29.
 */
const ROW_TITLE_X = [13];

/*
 * Bigger and bolder than the 8.5px/600 this drew until 2026-10-26. The titles
 * lead at 10px; the band labels sit at 9px, which is the largest that still
 * leaves "B-High" (about 31px) inside a cell.
 */
/*
 * 11px, up a point on 2026-10-26: "make small LDL P percentile text one point
 * bigger, it's hard to read". Rotated type is harder to read than horizontal
 * type at the same size, and this is the only rotated text in the app. The
 * column title moves with it so the two axis names stay one typographic voice.
 */
const AXIS_TITLE_SIZE = '11';
const BAND_LABEL_SIZE = '9';

const CELL_W =
  (W - GUTTER - (COL_LABELS.length - 1) * GAP) / COL_LABELS.length;
const GRID_H =
  ROW_LABELS.length * CELL_H + (ROW_LABELS.length - 1) * GAP;

/*
 * Inactive cells carry their OWN color at low opacity rather than a flat gray.
 *
 * Flat gray hid the thing the map is for: with the other cells blank, nothing
 * told the reader which corner is the good one, so the filled cell was a color
 * with no scale behind it (Maryann, 2026-10-23). Tinted, the grid reads as its
 * own legend.
 */
const IDLE_OPACITY = 0.22;
const IDLE_TEXT = '#9b948e';
const INK = '#3a3a3a';
/*
 * Both NMR count axes report in nmol/L, so the unit is a constant rather than
 * a per-row field. If a third axis in different units ever joins the readout,
 * this has to move onto the row.
 */
const UNITS = 'nmol/L';
/**
 * What the top of this panel IS, said once above the readings.
 *
 * Maryann, 2026-10-27, after asking what the point of the top section was and
 * whether it should be a cardiovascular-risk gauge: "I like the top section
 * saying 'atherogenic particle burden'."
 *
 * \u26a0 IT IS A MEASUREMENT, NOT A RISK ESTIMATE, and that is the whole reason
 * for the wording. A gauge reading "Cardiovascular Risk is Highly Likely" was
 * the alternative and it cannot be built from this panel: every validated
 * instrument (PREVENT, the Pooled Cohort Equations) needs age, sex, smoking,
 * blood pressure, diabetes status and eGFR, and NONE of them takes LDL-P or
 * apoB as an input at all. The 2018 AHA/ACC framework files apoB as a
 * risk-ENHANCING factor, applied after a calculator has already placed someone
 * at borderline or intermediate risk. "Atherogenic particle burden" is what
 * these two axes actually measure, and it claims nothing further.
 */
const SECTION_EYEBROW = 'Atherogenic Particle Burden';

/* The same blue .anemiaStatusGuageLabel gives the headline's reading. */
const READING_BLUE = '#337ab7';

/*
 * The same blood-drop marker every reference-range bar in the app draws, at its
 * intrinsic 15x20. It marks the client's cell in the grid.
 */
const bloodDrop = require('../../assets/imgs/blood_drop_black.png');
/*
 * Scaled DOWN from the asset's intrinsic 15x20, keeping its 3:4 ratio, so it
 * fits inside a 22px cell and reads as a marker rather than as the thing being
 * looked at. Dimensions are still declared, which is what stops the
 * 0x0-until-decoded measurement problem bloodDropLabel.ts documents.
 */
const DROP_W = 10.5;
const DROP_H = 14;

/** Which band of the printed ladder a score falls in, in the lab's own terms. */
export function lpIrPercentileWording(score: number): string {
  if (score < 27) return 'below the 25th percentile';
  if (score < 45) return 'between the 25th and 50th percentiles';
  if (score < 63) return 'between the 50th and 75th percentiles';
  return 'at or above the 75th percentile';
}

type Props = {
  map: LipidParticleMap;
  /** The LP-IR Score for this test, when one was reported. */
  lpIrScore?: number;
};

export class LipidParticleHero extends React.Component<Props, {}> {
  /**
   * The grid, 5 columns of LDL-P band by 4 rows of Small LDL-P zone.
   *
   * An axis the map could not read draws its labels in gray and leaves the
   * whole grid idle rather than disappearing: a client with LDL-P and no small
   * count should still see that one axis was answered and the other was not.
   */
  renderMap() {
    const { map } = this.props;

    const countKnown = map.countValue != null;
    const smallKnown = map.smallCount != null;
    const activeCol = countKnown ? ldlPBandIndex(map.countValue!) : -1;
    const activeRow = smallKnown ? smallLdlPBandIndex(map.smallCount!) : -1;

    const svgH = GRID_TOP + GRID_H + 2;
    const colX = (col: number) => GUTTER + col * (CELL_W + GAP);
    const rowY = (row: number) => GRID_TOP + row * (CELL_H + GAP);

    return (
      <svg
        width={W}
        height={svgH}
        viewBox={`0 0 ${W} ${svgH}`}
        role="img"
        aria-label={`Particle map: ${map.label}`}
      >
        {/* The column axis, named over the top of its own labels. */}
        <text
          x={GUTTER + (W - GUTTER) / 2}
          y={11}
          textAnchor="middle"
          fontSize={AXIS_TITLE_SIZE}
          fontWeight="700"
          fill={countKnown ? INK : IDLE_TEXT}
        >
          {COL_AXIS_TITLE}
        </text>

        {/*
          The row axis, rotated up the left edge. Two lines because "Small
          LDL-P percentile" is about 108px set at 10px and the grid is only
          97px tall, so one line would run past both ends of it.
        */}
        {ROW_AXIS_TITLE.map((line, i) => {
          const cy = GRID_TOP + GRID_H / 2;
          return (
            <text
              key={line}
              x={ROW_TITLE_X[i]}
              y={cy}
              transform={`rotate(-90 ${ROW_TITLE_X[i]} ${cy})`}
              textAnchor="middle"
              fontSize={AXIS_TITLE_SIZE}
              fontWeight="700"
              fill={smallKnown ? INK : IDLE_TEXT}
            >
              {line}
            </text>
          );
        })}

        {COL_LABELS.map((label, col) => (
          <text
            key={label}
            x={colX(col) + CELL_W / 2}
            y={25}
            textAnchor="middle"
            fontSize={BAND_LABEL_SIZE}
            fontWeight="700"
            fill={countKnown ? INK : IDLE_TEXT}
          >
            {label}
          </text>
        ))}

        {ROW_LABELS.map((label, row) => (
          <text
            key={label}
            x={GUTTER - 5}
            y={rowY(row) + CELL_H / 2 + 3}
            textAnchor="end"
            fontSize={BAND_LABEL_SIZE}
            fontWeight="700"
            fill={smallKnown ? INK : IDLE_TEXT}
          >
            {label}
          </text>
        ))}

        {ROW_LABELS.map((_, row) =>
          COL_LABELS.map((__, col) => {
            const active = row === activeRow && col === activeCol;
            return (
              <rect
                key={`${row}-${col}`}
                x={colX(col)}
                y={rowY(row)}
                width={CELL_W}
                height={CELL_H}
                rx={3}
                fill={SEVERITY_FILL[cellSeverity(col, row)]}
                fillOpacity={active ? 1 : IDLE_OPACITY}
                stroke={active ? INK : '#e9e5e0'}
                strokeWidth={active ? 1.6 : 0.75}
              />
            );
          }),
        )}

        {/*
          The client's position, drawn as the app's own marker rather than as a
          filled cell alone: with twenty cells the tinted column already carries
          the color, so the drop is what says "here".
        */}
        {activeCol >= 0 && activeRow >= 0 && (
          <image
            href={bloodDrop}
            x={colX(activeCol) + CELL_W / 2 - DROP_W / 2}
            y={rowY(activeRow) + CELL_H / 2 - DROP_H / 2}
            width={DROP_W}
            height={DROP_H}
          />
        )}
      </svg>
    );
  }

  /**
   * The grid's two axes, by value, one BULLETED LINE EACH.
   *
   * They shared a centered line joined by a middot until 2026-10-26, when
   * Maryann asked for "small LDLP on its own line ... give them both bullets,
   * and make them bold and in bigger text". One line per axis also means the
   * second value no longer wraps at an arbitrary point: joined, the pair ran
   * past 262px whenever both counts were four digits.
   *
   * ── Three weights on one line, which is the point ────────────────────────
   * "let's unbold the units ... put the items in parentheses and the
   * parentheses after it in blue" (Maryann, 2026-10-26). So each line carries:
   *
   *   \u2022  LDL-P 1585   nmol/L   (Borderline-High)
   *      bold        regular   bold + brand blue
   *
   * The unit is the least important token on the line and was competing with
   * the number at the same weight. The band word is the reading, so it takes
   * the same blue the headline's reading uses, #337ab7 from
   * .anemiaStatusGuageLabel, rather than a blue of its own.
   */
  renderReadout() {
    const { map } = this.props;
    const rows: { marker: string; value: number }[] = [];
    if (map.countValue != null) {
      rows.push({ marker: 'LDL-P', value: map.countValue });
    }
    /*
     * The second axis, with its zone, so the row the drop sits in has both its
     * number and the lab's word for it. The grid's row labels are shortened to
     * fit the gutter, so this line is where "25th-50th" is spelled out.
     */
    if (map.smallCount != null) {
      rows.push({ marker: 'Small LDL-P', value: map.smallCount });
    }
    /*
     * Mean diameter is deliberately NOT here. The grid's two axes are the two
     * counts, so a third number under it read as a third axis (Maryann,
     * 2026-10-23). LDL Size still has its own area bar, and still appears as a
     * marker row in any pattern that cites it.
     */
    if (!rows.length) return null;
    return (
      <div
        style={{
          marginTop: '6px',
          /*
           * Left-justified TO THE TABLE, not to the card (Maryann, 2026-10-26).
           * The grid's first cell starts at GUTTER inside the SVG, so the
           * bullets start there too and the block reads as one object.
           */
          marginLeft: `${GUTTER}px`,
        }}
      >
        {rows.map(row => (
          <div
            key={row.marker}
            style={{
              fontSize: '12px',
              color: INK,
              textAlign: 'left',
              /*
               * A hanging indent, so a line that does wrap lines up under its
               * own text rather than under the bullet.
               */
              paddingLeft: '12px',
              textIndent: '-12px',
              lineHeight: 1.45,
            }}
          >
            {'\u2022  '}
            <span style={{ fontWeight: 700 }}>
              {row.marker} {row.value}
            </span>
            <span style={{ fontWeight: 400 }}> {UNITS}</span>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const { map } = this.props;
    if (!map) return null;
    return (
      <div className="lipidParticleHero">
        {/*
          The gauge's own two-line treatment, and its two classes, so this
          headline reads identically to "Chronic Dehydration is / Highly Likely
          (95%)" in the sibling panels: a CONSTANT naming what was measured,
          then the variable reading beneath it in the brand blue.

          The reading is the lab's two band words, one per axis, in the same
          order the stem names them (particleCellName).
        */}
        {/*
          15px, up two points from the shared class's 13px (Maryann,
          2026-10-26). Overridden HERE rather than in bloodwork-anemia-status.css
          because that class is the headline of all six gauge panels: Anemia,
          WBC, Glucose, Adrenal, Iron and Hydration would all have grown with it.
        */}
        <div
          style={{
            fontSize: '10.5px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: '#766d66',
            textAlign: 'center',
            marginBottom: '3px',
          }}
        >
          {SECTION_EYEBROW}
        </div>
        <div
          className="anemiaStatusGuageLabelContainer"
          style={{ fontWeight: 700, fontSize: '15px' }}
        >
          {map.headlineRows.map(row => (
            <div key={row.label} style={{ paddingTop: '1px' }}>
              {row.label}{' '}
              <span style={{ color: READING_BLUE }}>{row.reading}</span>
            </div>
          ))}
        </div>
        {this.renderMap()}
        {this.renderReadout()}
      </div>
    );
  }
}
